import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useContext, useEffect, useMemo, useState } from "react";
import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./forestManagement";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { BEModulesClassNames } from "@utils/moduleList";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetForestDisturbances,
	useContextAwareGetForestManagementDefaults,
	useContextAwareGetForestManagement,
	useUpdateForestDisturbanceMutation,
	useUpdateForestManagementMutation,
} from "@hooks/index";
import type {
	ModuleFull,
} from "./forestManagementTypes";
import { useContextAwareGetLandUseChange } from "@hooks/index";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import ForestManagementTierOne from "./ForestManagementTierOne";
import ModuleContext from "../../ModuleContext";
import GenericTierTwo from "../GenericTierTwo";

const ForestManagement = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find((module) => module.id === FEModules.ForestManagement)?.uniqueId;

	const {
		data: forestData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetForestManagement(id ?? 0, { refetchOnMountOrArgChange: true });
	const {
		data: disturbancesData,
		isLoading: isLoadingDisturbances,
		isError: isErrorDisturbances,
		error: errorDisturbances,
	} = useContextAwareGetForestDisturbances({ activityId: activeActivityId ?? 0 }, {
		refetchOnMountOrArgChange: true,
	});
	const [
		updateForest,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateForestManagementMutation();
	const [
		updateDisturbance,
		{
			error: updateErrorDisturbance,
			isError: isUpdateErrorDisturbance,
			// isLoading: isLoadingUpdateDisturbance,
		},
	] = useUpdateForestDisturbanceMutation();

	const { data: parentDefaults, isError: isDefaultsError, error: defaultsError } = useContextAwareGetForestManagementDefaults(id ?? -1, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });
	const { belongsToLuc } = useContext(ModuleContext)

	const isForestRemainsForest = useMemo(() => belongsToLuc === undefined || (belongsToLuc?.is_start && (belongsToLuc?.is_w || belongsToLuc?.is_wo)), [belongsToLuc])
	const { moduleSchema, t2Schema } = useMemo(
		() => getSchemas(isForestRemainsForest ?? false),
		[isForestRemainsForest],
	);
	const [initValues, setInitValues] = useState<ModuleFull>({
		parent: initialValues,
		tiertwo: t2InitialValues,
		disturbances: [],
	});

	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDisturbances],
		isMutateError: isUpdateError || isUpdateErrorDisturbance,
		mutateError: updateError ?? updateErrorDisturbance,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { errorMsg: distBlockingError } = useErrorMessage({
		isError: isErrorDisturbances,
		error: errorDisturbances,
	});

	useEffect(() => {
		if (forestData)
			setInitValues((cur) => ({
				...cur,
				parent: forestData.parent,
				tiertwo: { ...forestData.tiertwo, ...parentDefaults },
			}));

	}, [forestData, parentDefaults]);

	useEffect(() => {
		if (!disturbancesData || !forestData) return;
		processModuleThreads({
			parent: forestData?.parent,
			disturbances: disturbancesData
		});
	}, [disturbancesData, processModuleThreads, forestData]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const parent = { ...values.parent };
		const parentPayload: ModuleFull = {
			...values,
			parent,
		};

		try {
			const disturbancePromises = values.disturbances
				.filter(disturbance => disturbance.id)
				.map(disturbance => updateDisturbance({
					body: disturbance,
					activityId: activeActivityId ?? 0,
				}));

			await Promise.all(disturbancePromises);
			await new Promise(resolve => setTimeout(resolve, 500));
			if (values.parent.id) {
				await updateForest({
					...parentPayload,
					activityId: activeActivityId
				});
			}

			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema(belongsToLuc)}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<ForestManagementTierOne
					title="project.forest_management"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.ForestManagement}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError ?? distBlockingError}
					isError={isUpdateError || isUpdateErrorDisturbance}
					setInitValues={setInitValues}
					initValues={initValues}
				/>

				<GenericTierTwo
					title="project.forest_management"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg}
				/>
			</>
		</Formik>
	);
};

export default ForestManagement;
